import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CloudDownload,
  KeyRound,
  Loader2,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import type {
  RemoteBackupSet,
  RestoreReadiness,
  RestoreResult,
  RestoreStorageInput,
  StagedBackup,
} from '@simpleacs/shared';
import { Button, Card, Field, Input, Pill } from '../components/ui';
import { formatBytes, formatTimestamp } from '../lib/format';

/**
 * Restoring this platform onto a clean install.
 *
 * Reachable WITHOUT a session, and that is the whole point: the machine this
 * page exists for has no accounts yet. What stands in for a login is a token
 * only root on this box can read, and a hard refusal to run on any install
 * that already holds data — both enforced by the API, not here. This page's
 * job is to make each check visible on its own, because an operator in the
 * middle of a disaster needs to see WHICH one failed, not a single red cross.
 *
 * It also has to be honest about the restart. The API holds an open handle on
 * the database; the swap ends with the process exiting so systemd brings it
 * back against the restored file. Between those two moments every request
 * fails, and a page that did not say so would look like it had hung at the
 * worst possible time.
 */

const RESTORE_MEDIA_TYPE = 'application/vnd.simpleacs.backup';

// --- a tiny client of its own -------------------------------------------------
// The shared `api` helper sends no custom headers and JSON-encodes everything.
// This surface needs `x-restore-token` on every call and a raw body on one.

interface ApiFailure {
  error?: { message?: string };
}

async function call<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const { token, headers, ...rest } = init;
  const res = await fetch(`/api/platform-restore${path}`, {
    ...rest,
    headers: {
      ...(headers ?? {}),
      ...(token ? { 'x-restore-token': token } : {}),
    },
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      res.status === 502 || res.status === 503
        ? 'The API is not answering — it may be restarting.'
        : res.statusText || 'The server returned something this page cannot read.',
    );
  }
  if (!res.ok) throw new Error((parsed as ApiFailure)?.error?.message ?? res.statusText);
  return parsed as T;
}

const json = <T,>(path: string, token: string, body: unknown) =>
  call<T>(path, {
    token,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

// --- page ---------------------------------------------------------------------

export function RestorePage() {
  const [readiness, setReadiness] = useState<RestoreReadiness | null>(null);
  const [readinessError, setReadinessError] = useState<string | null>(null);

  const [token, setToken] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [staged, setStaged] = useState<StagedBackup | null>(null);
  const [result, setResult] = useState<RestoreResult | null>(null);

  useEffect(() => {
    void call<RestoreReadiness>('/status')
      .then(setReadiness)
      .catch((err: Error) => setReadinessError(err.message));
  }, []);

  return (
    <div className="min-h-full bg-canvas px-4 py-10">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-6 text-primary" />
            <h1 className="text-lg font-semibold text-ink">Restore this server from a backup</h1>
          </div>
          <p className="text-sm text-muted">
            For a fresh install that is taking over from a server you have lost. It brings back the
            database — every OLT, ONU, subscriber, invoice and setting the backup holds. It will not
            run on an installation that already has data in it, and it never touches a chassis.
          </p>
        </header>

        {readinessError && <Notice tone="danger">{readinessError}</Notice>}

        {readiness && !readiness.ready && (
          <Card title="This server cannot be restored onto">
            <div className="space-y-3 px-5 py-4 text-sm">
              {readiness.blockers.map((line) => (
                <p key={line} className="text-muted">
                  {line}
                </p>
              ))}
            </div>
          </Card>
        )}

        {readiness?.ready && !result && (
          <>
            <TokenCard
              token={token}
              setToken={setToken}
              unlocked={unlocked}
              onUnlocked={() => setUnlocked(true)}
            />
            {unlocked && !staged && (
              <SourceCards token={token} onStaged={setStaged} maxBytes={readiness.maxUploadBytes} />
            )}
            {unlocked && staged && (
              <>
                <StagedCard
                  staged={staged}
                  token={token}
                  onDiscard={() => setStaged(null)}
                  onManifest={setStaged}
                />
                {staged.ready && (
                  <ApplyCard staged={staged} token={token} onDone={setResult} />
                )}
              </>
            )}
          </>
        )}

        {result && <ResultCard result={result} />}
      </div>
    </div>
  );
}

// --- step 1: the access anchor ------------------------------------------------

function TokenCard({
  token,
  setToken,
  unlocked,
  onUnlocked,
}: {
  token: string;
  setToken: (v: string) => void;
  unlocked: boolean;
  onUnlocked: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function unlock() {
    setBusy(true);
    setError(null);
    try {
      await json('/unlock', token, {});
      onUnlocked();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check that token.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="1 · Prove you are at this server"
      action={unlocked ? <Pill tone="success">Unlocked</Pill> : null}
    >
      <div className="space-y-4 px-5 py-4">
        <p className="text-sm text-muted">
          Read the restore token on this machine, at the console or over SSH as root:
        </p>
        <pre className="rounded-lg bg-surface-2 px-3 py-2 text-xs text-ink">
          sudo cat /root/restore-token.txt
        </pre>
        <p className="text-xs text-faint">
          This is what stops whoever reaches a fresh server first from uploading their own backup
          and owning it. It is not the encryption key and it protects nothing else — if the file is
          missing, run <code>sudo simpleacs-restore-token</code> to mint a new one.
        </p>
        <Field label="Restore token">
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="paste the token"
            autoComplete="off"
            spellCheck={false}
            disabled={unlocked}
          />
        </Field>
        {error && <Notice tone="danger">{error}</Notice>}
        {!unlocked && (
          <Button variant="primary" loading={busy} disabled={!token.trim()} onClick={() => void unlock()}>
            Unlock
          </Button>
        )}
      </div>
    </Card>
  );
}

// --- step 2: where the backup comes from --------------------------------------

function SourceCards({
  token,
  onStaged,
  maxBytes,
}: {
  token: string;
  onStaged: (s: StagedBackup) => void;
  maxBytes: number;
}) {
  const [mode, setMode] = useState<'upload' | 'remote'>('upload');
  return (
    <Card
      title="2 · Choose the backup"
      action={
        <div className="flex gap-1">
          <Button size="sm" variant={mode === 'upload' ? 'primary' : 'secondary'} onClick={() => setMode('upload')}>
            Upload a file
          </Button>
          <Button size="sm" variant={mode === 'remote' ? 'primary' : 'secondary'} onClick={() => setMode('remote')}>
            From storage
          </Button>
        </div>
      }
    >
      {mode === 'upload' ? (
        <UploadPanel token={token} onStaged={onStaged} maxBytes={maxBytes} />
      ) : (
        <RemotePanel token={token} onStaged={onStaged} />
      )}
    </Card>
  );
}

function UploadPanel({
  token,
  onStaged,
  maxBytes,
}: {
  token: string;
  onStaged: (s: StagedBackup) => void;
  maxBytes: number;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      // The body is the file itself, streamed by the browser — never read into
      // a string or base64, which for 44 MB would be a tab that stops
      // responding before it ever reaches the network.
      const staged = await call<StagedBackup>('/upload', {
        token,
        method: 'POST',
        headers: { 'Content-Type': RESTORE_MEDIA_TYPE, 'x-backup-filename': file.name },
        body: file,
      });
      onStaged(staged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 px-5 py-4">
      <p className="text-sm text-muted">
        The <code>database.sqlite</code> out of a backup set. Up to {formatBytes(maxBytes)}; it is
        written straight to this server&rsquo;s disk rather than held in memory.
      </p>
      <input
        type="file"
        className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />
      {file && (
        <p className="text-xs text-faint">
          {file.name} · {formatBytes(file.size)}
        </p>
      )}
      {error && <Notice tone="danger">{error}</Notice>}
      <Button
        variant="primary"
        icon={<Upload className="size-4" />}
        loading={busy}
        disabled={!file}
        onClick={() => void send()}
      >
        Upload and check
      </Button>
      {busy && (
        <p className="text-xs text-faint">
          Uploading. A large snapshot takes a while and the page will not respond until it lands.
        </p>
      )}
    </div>
  );
}

const EMPTY_STORAGE: RestoreStorageInput = {
  driver: 's3',
  s3: {
    endpoint: '',
    region: 'us-east-1',
    bucket: '',
    accessKeyId: '',
    secretAccessKey: '',
    forcePathStyle: true,
  },
};

function RemotePanel({ token, onStaged }: { token: string; onStaged: (s: StagedBackup) => void }) {
  const [input, setInput] = useState<RestoreStorageInput>(EMPTY_STORAGE);
  const [sets, setSets] = useState<RemoteBackupSet[] | null>(null);
  const [busy, setBusy] = useState<'test' | 'list' | 'fetch' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const s3 = input.s3 ?? EMPTY_STORAGE.s3!;
  const setS3 = (patch: Partial<typeof s3>) => setInput({ ...input, driver: 's3', s3: { ...s3, ...patch } });
  const bunny = input.bunny ?? { host: 'storage.bunnycdn.com', zone: '', accessKey: '' };
  const setBunny = (patch: Partial<typeof bunny>) =>
    setInput({ ...input, driver: 'bunny', bunny: { ...bunny, ...patch } });

  async function run(kind: 'test' | 'list') {
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      if (kind === 'test') {
        const res = await json<{ ok: boolean; detail: string }>('/remote/test', token, input);
        (res.ok ? setNotice : setError)(res.detail);
      } else {
        const res = await json<{ items: RemoteBackupSet[] }>('/remote/sets', token, input);
        setSets(res.items);
        if (res.items.length === 0) setNotice('No platform backup sets in that store.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The store did not answer.');
    } finally {
      setBusy(null);
    }
  }

  async function fetchSet(prefix: string) {
    setBusy('fetch');
    setError(null);
    try {
      onStaged(await json<StagedBackup>('/remote/fetch', token, { ...input, prefix }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The set could not be downloaded.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4 px-5 py-4">
      <p className="text-sm text-muted">
        Your storage credentials live <em>inside</em> the backup, so this server does not have them
        yet. Type them here — they are used to list and download the set, and then discarded. The
        real settings come back with the database.
      </p>

      <div className="flex gap-1">
        {(['s3', 'bunny', 'local'] as const).map((driver) => (
          <Button
            key={driver}
            size="sm"
            variant={input.driver === driver ? 'primary' : 'secondary'}
            onClick={() => setInput({ ...input, driver })}
          >
            {driver === 's3' ? 'S3-compatible' : driver === 'bunny' ? 'bunny.net' : 'Local directory'}
          </Button>
        ))}
      </div>

      {input.driver === 's3' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Endpoint">
            <Input value={s3.endpoint} placeholder="https://ewr1.vultrobjects.com" onChange={(e) => setS3({ endpoint: e.target.value })} />
          </Field>
          <Field label="Bucket">
            <Input value={s3.bucket} onChange={(e) => setS3({ bucket: e.target.value })} />
          </Field>
          <Field label="Region">
            <Input value={s3.region} onChange={(e) => setS3({ region: e.target.value })} />
          </Field>
          <Field label="Access key ID">
            <Input value={s3.accessKeyId} autoComplete="off" onChange={(e) => setS3({ accessKeyId: e.target.value })} />
          </Field>
          <Field label="Secret access key">
            <Input type="password" value={s3.secretAccessKey} autoComplete="off" onChange={(e) => setS3({ secretAccessKey: e.target.value })} />
          </Field>
        </div>
      )}

      {input.driver === 'bunny' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Storage host">
            <Input value={bunny.host} onChange={(e) => setBunny({ host: e.target.value })} />
          </Field>
          <Field label="Zone">
            <Input value={bunny.zone} onChange={(e) => setBunny({ zone: e.target.value })} />
          </Field>
          <Field label="Access key">
            <Input type="password" value={bunny.accessKey} autoComplete="off" onChange={(e) => setBunny({ accessKey: e.target.value })} />
          </Field>
        </div>
      )}

      {input.driver === 'local' && (
        <Field label="Directory on this server" hint="Where the backup sets were copied to.">
          <Input
            value={input.local?.dir ?? ''}
            placeholder="/opt/simpleacs-files"
            onChange={(e) => setInput({ ...input, driver: 'local', local: { dir: e.target.value } })}
          />
        </Field>
      )}

      <div className="flex gap-2">
        <Button loading={busy === 'test'} onClick={() => void run('test')}>
          Test
        </Button>
        <Button variant="primary" loading={busy === 'list'} onClick={() => void run('list')}>
          List backups
        </Button>
      </div>

      {notice && <Notice tone="info">{notice}</Notice>}
      {error && <Notice tone="danger">{error}</Notice>}

      {sets && sets.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {sets.map((set) => (
            <li key={set.prefix} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">
                  {set.takenAt ? formatTimestamp(set.takenAt) : set.backupId}
                </p>
                <p className="truncate text-xs text-faint">
                  {set.dbBytes ? formatBytes(set.dbBytes) : 'size unknown'}
                  {set.version ? ` · v${set.version}` : ''}
                  {/* The shelf, where the set carries one — `daily` says more
                      than `scheduled`, so it replaces rather than joins it. */}
                  {set.tier ? ` · ${set.tier}` : set.kind ? ` · ${set.kind}` : ''}
                  {set.problem ? ` · ${set.problem}` : ''}
                </p>
              </div>
              <Button
                size="sm"
                icon={<CloudDownload className="size-4" />}
                loading={busy === 'fetch'}
                onClick={() => void fetchSet(set.prefix)}
              >
                Use this
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- step 3: what the checks found --------------------------------------------

function StagedCard({
  staged,
  token,
  onDiscard,
  onManifest,
}: {
  staged: StagedBackup;
  token: string;
  onDiscard: () => void;
  onManifest: (s: StagedBackup) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function attach(file: File) {
    setBusy(true);
    setError(null);
    try {
      onManifest(
        await json<StagedBackup>(`/upload/${staged.stagingId}/manifest`, token, {
          manifest: await file.text(),
        }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That manifest could not be read.');
    } finally {
      setBusy(false);
    }
  }

  const c = staged.contents;
  return (
    <Card
      title="3 · What this backup is"
      action={
        <Button size="sm" onClick={onDiscard}>
          Choose another
        </Button>
      }
    >
      <div className="space-y-4 px-5 py-4">
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Source" value={staged.sourceLabel} />
          <Row label="Size" value={formatBytes(staged.bytes)} />
          <Row
            label="Taken"
            value={staged.manifest?.takenAt ? formatTimestamp(staged.manifest.takenAt) : 'not recorded'}
          />
          <Row label="sha256" value={`${staged.sha256.slice(0, 16)}…`} />
          {c && <Row label="OLTs" value={String(c.olts)} />}
          {c && <Row label="ONUs" value={String(c.onus)} />}
          {c && <Row label="Accounts" value={String(c.users)} />}
          {c && <Row label="Gateways" value={String(c.sites)} />}
        </dl>

        <div className="space-y-2">
          <Check
            ok={staged.manifestMatch === true}
            unknown={staged.manifestMatch === null}
            label="sha256 matches the manifest"
          />
          <Check ok={staged.integrity.ok} label={`SQLite integrity — ${staged.integrity.detail}`} />
        </div>

        {staged.manifestMatch === null && (
          <div className="rounded-lg border border-line px-3 py-2.5">
            <p className="text-xs text-muted">
              Add <code>manifest.json</code> from the same set to compare the snapshot against its
              recorded sha256.
            </p>
            <input
              type="file"
              accept=".json,application/json"
              disabled={busy}
              className="mt-2 block w-full text-xs text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-xs file:text-ink"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void attach(f);
              }}
            />
          </div>
        )}

        {staged.problems.map((p) => (
          <Notice key={p} tone="danger">
            {p}
          </Notice>
        ))}
        {staged.warnings.map((w) => (
          <Notice key={w} tone="warn">
            {w}
          </Notice>
        ))}
        {error && <Notice tone="danger">{error}</Notice>}
      </div>
    </Card>
  );
}

// --- step 4: the key, and the swap --------------------------------------------

function ApplyCard({
  staged,
  token,
  onDone,
}: {
  staged: StagedBackup;
  token: string;
  onDone: (r: RestoreResult) => void;
}) {
  const [key, setKey] = useState('');
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unprovable, setUnprovable] = useState(false);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      onDone(
        await json<RestoreResult>('/apply', token, {
          stagingId: staged.stagingId,
          appSecretKey: key,
          acceptUnverifiableKey: accept,
        }),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The restore was refused.';
      // The API says so in words when there is no ciphertext to test against;
      // surfacing the acknowledgement only then keeps it from being a checkbox
      // people learn to tick.
      if (message.includes('no encrypted value')) setUnprovable(true);
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="4 · The encryption key">
      <div className="space-y-4 px-5 py-4">
        <p className="text-sm text-muted">
          <code>APP_SECRET_KEY</code> from the old server&rsquo;s <code>.env</code> — the installer
          also wrote it to <code>/root/key.txt</code> there. It is deliberately not inside the
          backup: the database in your bucket is encrypted with it, and a bucket holding both is one
          compromise away from plaintext.
        </p>
        <Field
          label="APP_SECRET_KEY"
          hint="Checked by decrypting a secret out of this backup. A wrong key is refused and nothing is changed."
        >
          <Input
            type="password"
            value={key}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setKey(e.target.value)}
          />
        </Field>

        {unprovable && (
          <label className="flex items-start gap-2 text-sm text-muted">
            <input
              type="checkbox"
              className="mt-1"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
            />
            <span>
              This backup holds nothing encrypted, so the key cannot be proved right or wrong. Go
              ahead anyway and make it this server&rsquo;s key.
            </span>
          </label>
        )}

        {error && <Notice tone="danger">{error}</Notice>}

        <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2.5 text-xs text-warn">
          When this succeeds the API restarts — it holds the database open and only a fresh process
          reads the restored file. Expect about ten seconds of the page not answering, then sign in
          with the credentials from the old server.
        </div>

        <Button
          variant="danger"
          icon={<KeyRound className="size-4" />}
          loading={busy}
          disabled={key.length === 0 || (unprovable && !accept)}
          onClick={() => void apply()}
        >
          Verify the key and restore
        </Button>
      </div>
    </Card>
  );
}

function ResultCard({ result }: { result: RestoreResult }) {
  const [back, setBack] = useState(false);

  // Poll the API back to life rather than leaving the operator guessing. It is
  // deliberately the health endpoint: it answers before anything else does.
  useEffect(() => {
    if (result.restart !== 'scheduled') return;
    const timer = setInterval(() => {
      void fetch('/api/health')
        .then((r) => {
          if (r.ok) {
            setBack(true);
            clearInterval(timer);
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [result.restart]);

  return (
    <Card
      title="Restored"
      action={back ? <Pill tone="success">API back up</Pill> : <Pill tone="warn">restarting…</Pill>}
    >
      <div className="space-y-4 px-5 py-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" />
          <p className="text-sm text-ink">
            {formatBytes(result.dbBytes)} restored from {result.restoredFrom}.{' '}
            {result.keyVerdict === 'proved'
              ? `The key was proved against ${result.keyProvedBy}.`
              : 'The key could not be proved — this backup held nothing encrypted.'}{' '}
            {result.envWritten
              ? `It was written to ${result.envPath} at mode 0600.`
              : 'It was NOT written to .env — see below.'}
          </p>
        </div>

        <ol className="space-y-2 text-sm text-muted">
          {result.nextSteps.map((step, i) => (
            <li key={step} className="flex gap-2">
              <span className="text-faint">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        {back && (
          <Button variant="primary" onClick={() => window.location.assign('/')}>
            Sign in
          </Button>
        )}
        {!back && (
          <p className="flex items-center gap-2 text-xs text-faint">
            <Loader2 className="size-3.5 animate-spin" /> Waiting for the API to come back.
          </p>
        )}
      </div>
    </Card>
  );
}

// --- small pieces -------------------------------------------------------------

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line/60 pb-1">
      <dt className="text-faint">{label}</dt>
      <dd className="truncate text-ink">{value}</dd>
    </div>
  );
}

function Check({ ok, unknown, label }: { ok: boolean; unknown?: boolean; label: string }) {
  return (
    <p className="flex items-start gap-2 text-sm">
      {unknown ? (
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
      ) : ok ? (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
      ) : (
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
      )}
      <span className={unknown ? 'text-warn' : ok ? 'text-muted' : 'text-danger'}>
        {unknown ? `${label} — not checked, no manifest` : label}
      </span>
    </p>
  );
}

function Notice({ tone, children }: { tone: 'danger' | 'warn' | 'info'; children: ReactNode }) {
  const cls =
    tone === 'danger'
      ? 'border-danger/30 bg-danger-soft text-danger'
      : tone === 'warn'
        ? 'border-warn/30 bg-warn-soft text-warn'
        : 'border-info/30 bg-info-soft text-info';
  return <div className={`rounded-lg border px-3 py-2 text-sm ${cls}`}>{children}</div>;
}
